/**
 * Thin typed web API client for the People surface (web/v2) — the person side
 * panel's two reads (epic 2026-09-21, sub-tasks 03 and 04):
 *
 *   GET /api/web/v2/people/{handle}?import_id=…   the aggregated person
 *   GET /api/web/v2/people/{handle}/relationship  Thrive & HubSpot history
 *
 * Same auth posture as `imports.client.ts`: cookie session through the shared
 * `makeAuthedFetch` (single-flight 401 → refresh → retry-once). Reads only; the
 * panel's actions go through the imports client, which already owns them.
 */
import type { PersonDetail, PersonRelationshipResponse } from "@nu-assist/contracts";
import { makeAuthedFetch } from "@nu-assist/shared-fe";

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface PeopleClientConfig {
  apiBase: string;
  fetchImpl?: FetchLike;
}

/** A non-2xx answer. `detail` is FastAPI's string `detail` when there is one —
 *  the relationship route's 503 always carries `nu_sync_unavailable`. */
export class PeopleApiError extends Error {
  constructor(
    readonly status: number,
    readonly url: string,
    readonly detail?: string
  ) {
    super(`People API request failed (${status})${detail ? `: ${detail}` : ""}`);
    this.name = "PeopleApiError";
  }
}

const PREFIX = "/api/web/v2/people";

export function createPeopleClient(cfg: PeopleClientConfig) {
  const doFetch: FetchLike =
    cfg.fetchImpl ??
    makeAuthedFetch({
      apiBase: cfg.apiBase,
      onSessionExpired: () => window.location.assign("/login"),
    });

  async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
    const url = `${cfg.apiBase}${path}`;
    const res = await doFetch(url, { method: "GET", credentials: "include", signal });
    if (!res.ok) {
      let detail: string | undefined;
      try {
        const body = (await res.clone().json()) as { detail?: unknown };
        if (typeof body?.detail === "string") detail = body.detail;
      } catch {
        // not JSON (an ALB error page) — the status is the message
      }
      throw new PeopleApiError(res.status, url, detail);
    }
    return (await res.json()) as T;
  }

  return {
    /** GET /people/{handle}. `importId` marks the list the panel is open over
     *  and returns the person's row in it as `current_list`. 404 = no list has
     *  this person. `signal` is TanStack Query's: stepping ‹ › past a person
     *  whose read is still in flight aborts it. */
    getPerson(
      handle: string,
      importId?: string | null,
      opts: { signal?: AbortSignal } = {}
    ): Promise<PersonDetail> {
      const qs = importId ? `?import_id=${encodeURIComponent(importId)}` : "";
      return get<PersonDetail>(`${PREFIX}/${encodeURIComponent(handle)}${qs}`, opts.signal);
    },

    /** POST /companies/logos/by-linkedin-ids: a logo URL per LinkedIn company
     *  id that has a stored logo (ChopLink captures them). The Experience rows
     *  show them instead of initials. At most 100 ids per call (the WAF body
     *  cap); ids without a logo are absent. */
    async jobLogos(linkedinIds: string[]): Promise<Record<string, string>> {
      const url = `${cfg.apiBase}/api/web/v2/companies/logos/by-linkedin-ids`;
      const res = await doFetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ linkedin_ids: linkedinIds.slice(0, 100) }),
      });
      if (!res.ok) throw new PeopleApiError(res.status, url);
      return ((await res.json()) as { urls: Record<string, string> }).urls;
    },

    /** GET /people/{handle}/relationship. 503 (`nu_sync_unavailable`) when
     *  nu-sync did not answer; a person nu-sync does not know is a 200 with
     *  empty arrays. */
    getRelationship(
      handle: string,
      opts: { signal?: AbortSignal } = {}
    ): Promise<PersonRelationshipResponse> {
      return get<PersonRelationshipResponse>(
        `${PREFIX}/${encodeURIComponent(handle)}/relationship`,
        opts.signal
      );
    },
  };
}

export type PeopleClient = ReturnType<typeof createPeopleClient>;

export function defaultPeopleClient(): PeopleClient {
  return createPeopleClient({ apiBase: import.meta.env.VITE_API_URL ?? "" });
}
